import { Toaster, TooltipProvider } from "@myela/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/components/theme-provider";
import { NotificationSocketProvider } from "@/components/notifications/NotificationSocketProvider";
import { AnnouncementGate } from "@/components/announcements/AnnouncementGate";
import { PwaUpdatePrompt } from "@/components/pwa/PwaUpdatePrompt";
import { OfflineBanner } from "@/components/pwa/OfflineBanner";
import { useState, type CSSProperties } from "react";

// The library's Toaster reads var(--popover) as a colour, but this portal keeps --popover as a raw
// HSL triplet for Tailwind v3, so the toast and its action button rendered see-through. Point it
// at the full-colour Myela tokens instead (passing `style` replaces the library's whole object).
const toasterTokens = {
    "--normal-bg": "var(--myela-popover)",
    "--normal-text": "var(--myela-popover-foreground)",
    "--normal-border": "var(--myela-border)",
    "--border-radius": "var(--radius)",
} as CSSProperties;

export default function Providers({ children }: { children: React.ReactNode }) {
    const [queryClient] = useState(() => new QueryClient());

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <NotificationSocketProvider>
                    <ThemeProvider>
                        <TooltipProvider>
                            {children}
                            <AnnouncementGate />
                            <OfflineBanner />
                            <PwaUpdatePrompt />
                            <Toaster richColors position="bottom-right" style={toasterTokens} />
                        </TooltipProvider>
                    </ThemeProvider>
                </NotificationSocketProvider>
            </AuthProvider>
        </QueryClientProvider>
    );
}
